import {
  ClientOrganizationTagsEnum,
  ClientOrganizationRoleEnum
} from '@DigitaleDoerfer/digitale-doerfer-api';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';

import { AccountBalanceIcon, EditIcon } from '@/assets';
import { useGetOrganizationById } from '@/features/api/hooks/grapevine-organization/useGetOrganizationById';
import { useGetOrganizationsInGeoArea } from '@/features/api/hooks/grapevine-organization/useGetOrganizationsInGeoArea';
import { useHighestGlobalRole } from '@/features/authorization/provider/useHighestGlobalRole';
import { OrganizationDetailActivitiesSection } from '@/features/profile-setting/components/organization-detail/OrganizationDetailActivitiesSection';
import { OrganizationDetailAreaSection } from '@/features/profile-setting/components/organization-detail/OrganizationDetailAreaSection';
import { OrganizationDetailManagementSection } from '@/features/profile-setting/components/organization-detail/OrganizationDetailManagementSection';
import { buildOrganizationHeaderTitle } from '@/features/profile-setting/components/OrganizationHeaderTitle';
import { ProfileSettingRoute } from '@/features/profile-setting/navigation/profileSettingRoute';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { NavigationBar } from '@/navigation/components/navbar/NavigationBar';
import { StatusBarBackground } from '@/navigation/components/navbar/StatusBarBackground';
import type { AppNativeStackScreenProps } from '@/navigation/types/navigation';
import { AppRoute, TabRoute } from '@/navigation/types/navigation';
import { GradientBackground } from '@/shared/components/GradientBackground';
import {
  OrganizationAccessGuard,
  OrganizationAccessProvider,
  useOrganizationAccess
} from '@/shared/components/organization';
import { OrganizationAccessState } from '@/shared/components/organization/organizationAccess';
import { StateView } from '@/shared/components/state-view/StateView';
import { isOrganizationType } from '@/shared/utils/organization';

const styles = StyleSheet.create({
  content: { paddingBottom: 32 }
});

type Props = AppNativeStackScreenProps<
  typeof ProfileSettingRoute.OrganizationDetail
>;

type ContentProps = {
  organizationId: string;
  navigation: Props['navigation'];
};

const VIEWER_ROLES = [
  ClientOrganizationRoleEnum.Funker,
  ClientOrganizationRoleEnum.Admin
];
const MANAGER_ROLES = [ClientOrganizationRoleEnum.Admin];

const OrganizationDetailContent: FC<ContentProps> = ({
  organizationId,
  navigation
}) => {
  const { t } = useTranslation(LocalizationNameSpace.ORGANIZATION, {
    keyPrefix: 'organization-detail'
  });
  const { t: tTags } = useTranslation(LocalizationNameSpace.ORGANIZATION, {
    keyPrefix: 'organization.tagLabels'
  });
  const { t: tRoles } = useTranslation(LocalizationNameSpace.ORGANIZATION, {
    keyPrefix: 'organization.roleLabels'
  });
  const { t: tGlobalRoles } = useTranslation(
    LocalizationNameSpace.ORGANIZATION,
    {
      keyPrefix: 'organization.globalRoleLabels'
    }
  );

  const globalRole = useHighestGlobalRole();

  const {
    data: organization,
    isLoading,
    refetch,
    isRefetching
  } = useGetOrganizationById(organizationId);

  // TODO: Deduplicate
  const orgGeoAreaIds: string[] = useMemo(() => {
    if (!organization?.geoAreaIds) {
      return [];
    }

    return organization.geoAreaIds.filter(
      (id): id is string => typeof id === 'string' && Boolean(id)
    );
  }, [organization]);

  const { data: territorialOrganizationsRaw } = useGetOrganizationsInGeoArea({
    geoAreaIds: orgGeoAreaIds,
    enabled: isOrganizationType(
      ClientOrganizationTagsEnum.TerritorialAuthority,
      organization
    )
  });

  // the backend expands the org's geo areas but cannot exclude the reference
  // organization itself (the endpoint has no reference-org parameter)
  const territorialOrganizations = useMemo(
    () =>
      territorialOrganizationsRaw?.filter((org) => org.id !== organizationId),
    [territorialOrganizationsRaw, organizationId]
  );

  const viewerAccess = useOrganizationAccess({ roles: VIEWER_ROLES });
  const managerAccess = useOrganizationAccess({ roles: MANAGER_ROLES });
  const canManage = managerAccess === OrganizationAccessState.Granted;

  const handleEditPress = useCallback(() => {
    navigation.push(ProfileSettingRoute.OrganizationEdit, {
      organizationId
    });
  }, [navigation, organizationId]);

  const handleBackPress = useCallback(() => {
    const { routes, index } = navigation.getState();
    if (index === 0 || routes[index - 1]?.name === AppRoute.Tabs) {
      navigation.popTo(AppRoute.Tabs, { screen: TabRoute.Profile });
    } else {
      navigation.goBack();
    }
  }, [navigation]);

  const handleAllFunks = useCallback(() => {
    navigation.push(ProfileSettingRoute.OrganizationPosts, {
      organizationId
    });
  }, [navigation, organizationId]);

  const handleMentioned = useCallback(() => {
    navigation.push(ProfileSettingRoute.OrganizationPostsWithActivities, {
      organizationId
    });
  }, [navigation, organizationId]);
  const handleMembers = useCallback(() => {
    navigation.push(ProfileSettingRoute.OrganizationMembers, {
      organizationId
    });
  }, [navigation, organizationId]);
  const handleStatisticsMail = useCallback(() => {
    navigation.push(ProfileSettingRoute.StatisticsEmail, {
      organizationId
    });
  }, [navigation, organizationId]);

  useEffect(() => {
    const primaryTag = organization?.tags?.[0];
    const role = organization?.role;
    const directRoleLabel = role ? tRoles(role) : undefined;
    const roleLabel = globalRole ? tGlobalRoles(globalRole) : directRoleLabel;
    const subTitle = buildOrganizationHeaderTitle({
      tagLabel: primaryTag ? tTags(primaryTag) : '',
      roleLabel
    });

    navigation.setOptions({
      header: () => (
        <StatusBarBackground>
          <NavigationBar
            onBackButtonPress={handleBackPress}
            title={organization?.name}
            subTitle={subTitle}
            picture={organization?.logoImage}
            pictureFallback={{ icon: AccountBalanceIcon }}
            titleVariant={'titleMedium'}
            buttons={
              canManage
                ? [
                    {
                      name: 'edit',
                      icon: EditIcon,
                      onPress: handleEditPress
                    }
                  ]
                : undefined
            }
          />
        </StatusBarBackground>
      )
    });
  }, [
    canManage,
    globalRole,
    handleBackPress,
    handleEditPress,
    navigation,
    organization,
    tTags,
    tRoles,
    tGlobalRoles
  ]);

  return (
    <GradientBackground>
      <StateView
        isLoading={isLoading}
        isRefetching={isRefetching}
        isEmpty={!isLoading && viewerAccess === OrganizationAccessState.Denied}
        onRefetch={refetch}
        stateLabels={{
          loading: t('state.loading', { returnObjects: true }),
          error: t('state.error', { returnObjects: true }),
          empty: t('state.noAccess', { returnObjects: true })
        }}
        backgroundColor="transparent"
        contentContainerStyle={styles.content}
      >
        <OrganizationAccessGuard roles={VIEWER_ROLES}>
          <OrganizationDetailActivitiesSection
            onAllFunksPress={handleAllFunks}
            onMentionedPress={handleMentioned}
          />
        </OrganizationAccessGuard>

        <OrganizationAccessGuard roles={VIEWER_ROLES}>
          <OrganizationDetailManagementSection
            onMembersPress={handleMembers}
            onStatisticsMailPress={handleStatisticsMail}
          />
        </OrganizationAccessGuard>

        <OrganizationDetailAreaSection
          organization={organization}
          territorialOrganizations={territorialOrganizations}
        />
      </StateView>
    </GradientBackground>
  );
};

export const OrganizationDetailScreen: FC<Props> = ({ route, navigation }) => {
  const { id: organizationId } = route.params;
  const { data: organization } = useGetOrganizationById(organizationId);

  return (
    <OrganizationAccessProvider organization={organization}>
      <OrganizationDetailContent
        organizationId={organizationId}
        navigation={navigation}
      />
    </OrganizationAccessProvider>
  );
};
